import React, { useMemo } from 'react';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { StockBadge, StockStatus } from '@components/atoms/StockBadge';
import { cn } from '@utils/cn';
import { MaterialCatalog, StockLevel } from '@models/warehouse.model';
import {
  AvailabilityIndex,
  CatalogAvailability,
  describeStockElsewhere,
  getCatalogAvailability,
} from '@utils/stockAvailability';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import WarehouseIcon from '@mui/icons-material/Warehouse';

export interface MaterialOption extends SelectOption {
  material: MaterialCatalog;
  stockLevel?: StockLevel;
  stockStatus: StockStatus;
  /** Warehouse-scoped in scoped mode, org-wide total in aggregate mode. */
  quantityAvailable: number;
  /** Org-wide breakdown — only populated when `availabilityIndex` is supplied. */
  availability?: CatalogAvailability;
}

interface MaterialSelectorProps {
  materials: MaterialCatalog[];
  stockLevels?: StockLevel[];
  value?: MaterialOption | null;
  onChange: (option: MaterialOption | null) => void;
  warehouseId?: string; // Filter stock by warehouse
  /**
   * Org-wide availability (see `@utils/stockAvailability`). When provided the
   * selector switches to aggregate mode: stock status reflects every warehouse,
   * so a material stored elsewhere is no longer labelled "Out of Stock".
   */
  availabilityIndex?: AvailabilityIndex;
  placeholder?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  error?: boolean;
  helperText?: string;
  label?: string;
  required?: boolean;
  showOutOfStock?: boolean; // Show out of stock items (disabled)
  allowOutOfStock?: boolean; // Allow selecting out of stock items (not disabled)
  className?: string;
}

const getStockStatus = (
  quantity: number,
  reorderPoint?: number,
): StockStatus => {
  if (quantity <= 0) return 'out_of_stock';
  if (reorderPoint && quantity <= reorderPoint) return 'low_stock';
  return 'in_stock';
};

export const MaterialSelector: React.FC<MaterialSelectorProps> = ({
  materials,
  stockLevels = [],
  value,
  onChange,
  warehouseId,
  availabilityIndex,
  placeholder = 'Select material...',
  isDisabled = false,
  isLoading = false,
  error = false,
  helperText,
  label,
  required = false,
  showOutOfStock = true,
  allowOutOfStock = false,
  className,
}) => {
  const isAggregate = !!availabilityIndex;

  // Build options with stock information
  const options = useMemo(() => {
    return materials.map((material) => {
      // Find stock level for this material (optionally filtered by warehouse)
      const stockLevel = stockLevels.find(
        (sl) =>
          sl.catalogId === material.catalogId &&
          (!warehouseId || sl.warehouseId === warehouseId),
      );

      const scopedAvailable = stockLevel
        ? Number(stockLevel.quantityOnHand) - Number(stockLevel.quantityReserved)
        : 0;

      const availability = isAggregate
        ? getCatalogAvailability(availabilityIndex, material.catalogId)
        : undefined;

      // In aggregate mode the headline number is the org-wide total, so stock
      // sitting in another warehouse no longer reads as "Out of Stock".
      const quantityAvailable = availability ? availability.totalAvailable : scopedAvailable;

      const stockStatus = getStockStatus(
        quantityAvailable,
        material.reorderPoint,
      );

      const isOutOfStock = stockStatus === 'out_of_stock';

      return {
        value: material.catalogId,
        label: material.name,
        material,
        stockLevel: stockLevel || availability?.warehousesWithStock[0]?.stockLevel,
        stockStatus,
        quantityAvailable,
        availability,
        isDisabled: allowOutOfStock ? false : (isOutOfStock && !showOutOfStock ? false : isOutOfStock),
      } as MaterialOption;
    });
  }, [
    materials,
    stockLevels,
    warehouseId,
    showOutOfStock,
    allowOutOfStock,
    availabilityIndex,
    isAggregate,
  ]);

  // Filter out of stock if not showing them
  const filteredOptions = showOutOfStock
    ? options
    : options.filter((opt) => opt.stockStatus !== 'out_of_stock');

  // Custom option renderer - context differentiates menu vs selected value
  const formatOptionLabel = (
    option: MaterialOption,
    { context }: { context: 'menu' | 'value' },
  ) => {
    // Compact rendering for the selected value in the control
    if (context === 'value') {
      return (
        <div className="flex items-center gap-2">
          <InventoryIcon className="text-emerald-500" sx={{ fontSize: 16 }} />
          <span className="truncate">{option.material.name}</span>
          <StockBadge status={option.stockStatus} size="sm" />
        </div>
      );
    }

    const warehouseCount = option.availability?.warehousesWithStock.length ?? 0;
    const locationHint =
      warehouseCount > 0
        ? warehouseCount === 1
          ? `In ${option.availability?.warehousesWithStock[0].warehouseName}`
          : describeStockElsewhere(option.availability as CatalogAvailability)
        : '';

    // Rich rendering for dropdown menu
    return (
      <div className="flex items-center gap-3 w-full py-0.5">
        <div className="w-8 h-8 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
          <InventoryIcon className="text-emerald-500" sx={{ fontSize: 16 }} />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <span
            className={cn(
              'text-sm font-medium truncate',
              option.isDisabled && 'text-gray-400 dark:text-gray-600',
            )}>
            {option.material.name}
          </span>
          {option.material.sku && (
            <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
              SKU: {option.material.sku}
            </span>
          )}
          {locationHint && (
            <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 truncate">
              <WarehouseIcon sx={{ fontSize: 12 }} />
              {locationHint}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span
            className={cn(
              'text-sm font-medium',
              option.isDisabled
                ? 'text-gray-400'
                : 'text-gray-700 dark:text-gray-300',
            )}>
            {option.quantityAvailable} {option.material.unit || 'units'}
          </span>
          <StockBadge status={option.stockStatus} size="sm" />
        </div>
      </div>
    );
  };

  const handleChange = (
    selected: any,
  ) => {
    if (!selected) {
      onChange(null);
      return;
    }
    // Find the full option
    const option = options.find((opt) => opt.value === selected.value);
    onChange(option || null);
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <ReactSelect
        options={filteredOptions}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isLoading={isLoading}
        error={error}
        helperText={helperText}
        isSearchable
        isClearable
        formatOptionLabel={formatOptionLabel as any}
      />
    </div>
  );
};

export default MaterialSelector;
